import React, { useState, useCallback } from 'react';
import { ThumbsUp, ThumbsDown, RotateCcw } from 'lucide-react';
import superjson from 'superjson';

interface MessageFeedbackProps {
  messageId: string;
  source: 'taskingbot' | 'taskingbot-task' | 'session';
  sourceId?: string | null;
  messageContent?: string;
  onRetry?: () => void;
  compact?: boolean;
  inline?: boolean;
}

export function MessageFeedback({
  messageId,
  source,
  sourceId,
  messageContent,
  onRetry,
  compact = false,
  inline = false,
}: MessageFeedbackProps) {
  const [rating, setRating] = useState<'up' | 'down' | null>(null);
  const [saving, setSaving] = useState(false);

  const handleRate = useCallback(async (newRating: 'up' | 'down') => {
    if (saving) return;
    setSaving(true);
    try {
      const res = await fetch('/_api/taskingbot/message-feedback', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: superjson.stringify({
          messageId,
          rating: newRating,
          source,
          sourceId,
          messageContent,
        }),
      });
      if (res.ok) {
        const text = await res.text();
        let data: any;
        try { data = superjson.parse(text); } catch { data = JSON.parse(text); }
        setRating(data.rating || null);
      }
    } catch {
      // Silent fail
    } finally {
      setSaving(false);
    }
  }, [messageId, source, sourceId, messageContent, saving]);

  const size = compact ? 15 : 17;
  const pad = compact ? '5px 8px' : '6px 10px';

  return (
    <div
      className="message-feedback-row"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: compact ? '2px' : '4px',
        marginTop: inline ? 0 : compact ? '8px' : '10px',
        paddingTop: inline ? 0 : compact ? '8px' : '10px',
        borderTop: inline ? 'none' : '1px solid rgba(128,128,128,0.15)',
      }}
    >
      <button
        onClick={() => handleRate('up')}
        disabled={saving}
        title="Good response"
        aria-label="Good response"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: pad,
          border: '1px solid transparent',
          borderRadius: '6px',
          background: rating === 'up' ? 'rgba(34,197,94,0.2)' : 'rgba(128,128,128,0.08)',
          color: rating === 'up' ? '#22c55e' : 'rgba(128,128,128,0.55)',
          cursor: 'pointer',
          transition: 'all 0.15s ease',
        }}
        onMouseEnter={(e) => {
          if (rating !== 'up') {
            if (rating !== 'up') (e.currentTarget as HTMLButtonElement).style.background = 'rgba(34,197,94,0.12)';
            if (rating !== 'up') (e.currentTarget as HTMLButtonElement).style.color = '#22c55e';
          }
        }}
        onMouseLeave={(e) => {
          if (rating !== 'up') {
            (e.currentTarget as HTMLButtonElement).style.background = 'rgba(128,128,128,0.08)';
            (e.currentTarget as HTMLButtonElement).style.color = 'rgba(128,128,128,0.55)';
          }
        }}
      >
        <ThumbsUp size={size} />
      </button>

      <button
        onClick={() => handleRate('down')}
        disabled={saving}
        title="Bad response"
        aria-label="Bad response"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: pad,
          border: '1px solid transparent',
          borderRadius: '6px',
          background: rating === 'down' ? 'rgba(239,68,68,0.2)' : 'rgba(128,128,128,0.08)',
          color: rating === 'down' ? '#ef4444' : 'rgba(128,128,128,0.55)',
          cursor: 'pointer',
          transition: 'all 0.15s ease',
        }}
        onMouseEnter={(e) => {
          if (rating !== 'down') {
            if (rating !== 'down') (e.currentTarget as HTMLButtonElement).style.background = 'rgba(239,68,68,0.12)';
            if (rating !== 'down') (e.currentTarget as HTMLButtonElement).style.color = '#ef4444';
          }
        }}
        onMouseLeave={(e) => {
          if (rating !== 'down') {
            (e.currentTarget as HTMLButtonElement).style.background = 'rgba(128,128,128,0.08)';
            (e.currentTarget as HTMLButtonElement).style.color = 'rgba(128,128,128,0.55)';
          }
        }}
      >
        <ThumbsDown size={size} />
      </button>

      {onRetry && (
        <button
          onClick={onRetry}
          title="Retry"
          aria-label="Retry"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: pad,
            border: '1px solid transparent',
            borderRadius: '6px',
            background: 'rgba(128,128,128,0.08)',
            color: 'rgba(128,128,128,0.55)',
            cursor: 'pointer',
            transition: 'all 0.15s ease',
            marginLeft: '2px',
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background = 'rgba(128,128,128,0.15)';
            (e.currentTarget as HTMLButtonElement).style.color = 'rgba(128,128,128,0.75)';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background = 'rgba(128,128,128,0.08)';
            (e.currentTarget as HTMLButtonElement).style.color = 'rgba(128,128,128,0.55)';
          }}
        >
          <RotateCcw size={size} />
        </button>
      )}
    </div>
  );
}
